import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, interval, Observable, of, Subject } from 'rxjs';
import { map, startWith, take, takeUntil } from 'rxjs/operators';
import { Maintenance } from '../../../class/Maintenance';
import { DashboardCard } from '../../../class/Preference';
import { Unit } from '../../../database/models';
import { DevicesManagerService } from '../../../services/devices-manager.service';
import { DevicesStatsService } from '../../../services/devices-stats.service';
import { LoadingService } from '../../../services/loading.service';
import { MaintenanceService } from '../../../services/maintenance.service';
import { PermissionService } from '../../../services/permission.service';
import { PreferenceService } from '../../../services/preference.service';
import { RoutingService } from '../../../services/routing.service';
import UnitsService from '../../../services/units.service';
import HasPermissionDirective from '../../../utils/directives/has-permission.directive';
import { DEVICES } from '../../devices/devices-routing.constants';
import { movingStatusFromState } from '../../devices/park-status.constants';
import { MaintenanceChartComponent } from '../../shared/widget/maintenance-chart/maintenance-chart.component';
import { ParkKpiComponent } from '../../shared/widget/park-kpi/park-kpi.component';
import { WidgetLoaderComponent } from '../../shared/widget/widget-loader/widget-loader.component';
import { TreeComponent } from '../../tree/tree.component';

const STATS_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

@Component({
  selector: 'widget-state',
  templateUrl: './widget-state.component.html',
  styleUrls: ['./widget-state.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    ParkKpiComponent,
    WidgetLoaderComponent,
    TranslatePipe,
    HasPermissionDirective,
    MaintenanceChartComponent,
    TreeComponent,
    MatIcon,
    AsyncPipe,
  ],
})
export class WidgetStateComponent implements OnInit, OnDestroy {
  card = input<DashboardCard>();
  @Input() index: number;
  @Input() isEdit: boolean;

  public routingService = inject(RoutingService);
  private _unitsService = inject(UnitsService);
  private _devicesManagerService = inject(DevicesManagerService);
  private _deviceStatsService = inject(DevicesStatsService);
  private loadingService = inject(LoadingService);
  private preferenceService = inject(PreferenceService);
  public maintenanceService = inject(MaintenanceService);
  public permissionService = inject(PermissionService);
  private _router = inject(Router);

  public changingService = false;

  public uid_service = signal<number>(0);
  public unitSelected = computed(() => {
    const units = this._unitsService.$values();
    if (!units?.length) return null;
    return units.find(unit => unit.uid === this.uid_service());
  });
  private _$unitWithStats = this._deviceStatsService.$selectUnitWithStats;
  public time = this._deviceStatsService.selectTimeUsed;
  public $devicesMap = this._devicesManagerService.$devicesMap;

  public filterMaintenances$: Observable<Maintenance[]> = of([]);
  private _devicesSelected = new FormControl<number[]>(undefined);
  private componentDestroy$ = new Subject<void>();

  public devicesList: number[] = [];
  /**
   * Readiness is gated on $serviceTree, not $unitsById: the KPIs are aggregated per service by
   * DevicesStatsService, which reads $serviceTree exclusively and bails out while it is null.
   * $unitsById comes straight from the units liveQuery, so it turns truthy before loadData()'s
   * combineLatest has run legacyProcessServices — initComponent() would then compute nothing and,
   * having taken the "already loaded" branch, never listen to servicesLoadedEvent$ either.
   */
  public $serviceTree = this._unitsService.$serviceTree;

  ngOnInit(): void {
    if (this.$serviceTree() && this._devicesManagerService.$devicesMap()) {
      this.initComponent();
    } else {
      this.loadingService.servicesLoadedEvent$.pipe(take(1)).subscribe(() => {
        this.initComponent();
      });
    }
  }

  ngOnDestroy() {
    this.componentDestroy$.next();
    this.componentDestroy$.complete();
  }

  private initComponent() {
    if (this.card().card === 'maintenance-state') {
      this.uid_service.set(this.card().uid_service || 0);
      this.devicesList = this.retrieveDevicesSelected(this.devicesList, this.uid_service());

      this.filterMaintenances$ = combineLatest([
        this.maintenanceService.maintenanceListTodo$,
        this._devicesSelected.valueChanges.pipe(startWith('')),
      ]).pipe(
        takeUntil(this.componentDestroy$),
        map(([maintenanceList]) => {
          return maintenanceList.filter((maintenance: Maintenance) => {
            return this.filterService(maintenance, this.devicesList);
          });
        })
      );
    } else {
      this._deviceStatsService.changeBothUnitAndTimeUsed(
        this.card().uid_service || 0,
        (this.card().time as number) || 0
      );
      this.uid_service.set(this._$unitWithStats()?.uid || 0);
      // refresh park kpi every 5 minutes, keeping the current unit/time selection
      interval(STATS_REFRESH_INTERVAL_MS)
        .pipe(takeUntil(this.componentDestroy$))
        .subscribe(() => {
          this._deviceStatsService.changeBothUnitAndTimeUsed(
            this._$unitWithStats()?.uid ?? null,
            this._deviceStatsService.selectTimeUsed()
          );
        });
    }
  }

  private filterService(maintenance: Maintenance, devices: number[]): boolean {
    return devices.includes(maintenance.uid_device);
  }

  public changeParent(units: Unit[]) {
    if (units[0]?.uid !== this.uid_service()) {
      this.uid_service.set(units[0]?.uid);
      if (this.card().card === 'maintenance-state') {
        this.devicesList = [];
        this.devicesList = this.retrieveDevicesSelected(this.devicesList, this.uid_service() || 0);
        this._devicesSelected.setValue(this.devicesList);
      } else {
        this._deviceStatsService.changeBothUnitAndTimeUsed(this.uid_service() || 0, this.time());
      }
      this.savePreferenceUid();
      this.selectService();
    }
  }

  /**
   * Status card of the "Park state" widget → open the park filtered on that status.
   *
   * The status is handed to `DevicesManagerService` rather than to the URL or the preferences: it is
   * a one-off navigation intent, which the park consumes once at its initialisation.
   * No view is forced, the park restores the one used last.
   */
  public displayParkStatus(state: string) {
    // In edit mode the user is arranging their widgets: navigating away would be a trap.
    if (this.isEdit) return;

    const movingStatus = movingStatusFromState(state);
    if (!movingStatus) return;

    this._devicesManagerService.setPendingStatusFilter(movingStatus);
    this._router.navigate([DEVICES]);
  }

  public changeTime(daysBefore: number) {
    this._deviceStatsService.changeBothUnitAndTimeUsed(this.uid_service() || 0, daysBefore);
    this.preferenceService.mutatePreference(preference => {
      preference.dashboard[this.index].time = daysBefore;
    });
    this.preferenceService.savePreference('update');
    this.preferenceService.updateStorage();
  }

  private savePreferenceUid() {
    this.preferenceService.mutatePreference(preference => {
      preference.dashboard[this.index].uid_service = this.uid_service();
    });
    this.savePreference();
  }

  private savePreference() {
    this.preferenceService.savePreference('update');
    this.preferenceService.updateStorage();
  }

  public selectService() {
    this.changingService = !this.changingService;
  }

  public retrieveDevicesSelected(devices: number[], uid_service: number): number[] {
    if (this.uid_service() === 0) {
      devices = Object.keys(this._devicesManagerService.$devicesMap()).map(Number);
    } else {
      devices = devices.concat(this._unitsService.getDevices(uid_service).map(device => device.uid));
    }
    return devices;
  }
}
